import { useSelector } from 'react-redux'
import FormsyForm from 'formsy-react'
import replace from 'lodash/replace'
import PropTypes from 'prop-types'
import _isNil from 'lodash/isNil'
import _get from 'lodash/get'
import React from 'react'
import { Map } from 'immutable'
import { getFormattedLocaleDate, formatDateWithTimezonOffset } from '../../../services/date-time/index'
import { selectEmail, selectPrimaryLanguage } from '../../../services/user/selectors'
import { selectResolverQueryParamByKey } from '../../../services/resolver/selectors'
import { selectAuth } from '../../../services/auth/selectors'
import { GiftSelection } from '../GiftSelection/index'
import { selectStaticTextByKey } from '../../../services/static-text/selectors'
import { URL_GIFT_LP, URL_HOME } from '../../../services/url/constants'
import { GiftLayout } from '../GiftLayout/index'
import { TextInput } from '../../FormInput.v2/index'
import Button, { TYPES } from '../../Button/index'
import { selectIsBreakpoint } from '../../../services/app/selectors'
import { useGetPlanGridData } from '../../../services/plans/hooks'
import { BILLING_PLAN_GRID_LOCATION_GIFT } from '../../../services/plans/utils'
import { createAbsolute, getGiftLpUrl } from '../../../services/url/index'
import { GIFT_MOBILE_BREAKPOINT } from '../../../services/gift/index'
import {
  selectGiftToken,
  selectGiftPlan,
  selectGift,
  selectGiftSku,
} from '../../../services/gift/selectors'
import { get as getConfig } from '../../../config/index'
import {
  GIFT_COMPLETE_PAGE_STATIC_KEY as staticTextStoreKey,
} from './gift-complete-page-static-key'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])
const giftMultipleCurrenciesActive = !!_get(config, ['features', 'gift', 'multipleCurrencies'])

const GiftCompletePagePropTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
}

function CompleteSection ({
  staticText,
  selectedPlan,
  mobileView,
  token,
  gift,
}) {
  const userLanguage = useSelector(selectPrimaryLanguage)
  const email = useSelector(selectEmail)

  if (_isNil(selectedPlan)) {
    return null
  }

  const giftMembership = selectedPlan.get('title', '')
  const recipientEmail = gift.getIn(['details', 'recipientEmail'], '')
  const gifterEmail = gift.getIn(['details', 'gifterEmail'], '') || email
  const deliveryDate = gift.getIn(['details', 'deliveryDate'], undefined)
  const expires = gift.getIn(['orderData', 'gift', 'expiresAt']) || gift.getIn(['details', 'expiresAt'], undefined)
  const formattedSendDate = deliveryDate
    ? getFormattedLocaleDate(formatDateWithTimezonOffset(new Date(deliveryDate)), userLanguage)
    : null
  const formattedExpireDate = expires
    ? getFormattedLocaleDate(formatDateWithTimezonOffset(new Date(expires)), userLanguage)
    : null
  /* eslint-disable-next-line no-template-curly-in-string */
  const yourGiftMembership2 = replace(staticText.get('yourGiftMembership2'), '${ giftMembership }', giftMembership)
  /* eslint-disable-next-line no-template-curly-in-string */
  const yourGiftMembership4 = replace(staticText.get('yourGiftMembership4'), '${ recipientEmail }', recipientEmail)
  /* eslint-disable-next-line no-template-curly-in-string */
  const yourGiftMembership6 = replace(staticText.get('yourGiftMembership6'), '${ sendDate }', formattedSendDate)
  const link = createAbsolute(`/gift/redeem?token=${token}`)

  return (
    <div className="gift-complete">
      <p className="gift-complete__text">
        {recipientEmail && deliveryDate ? staticText.get('yourOrder') : (
          <>
            <span>{staticText.get('yourOrder2')}</span>
             {/* eslint-disable-next-line no-template-curly-in-string */}
            <span className="gift-complete__bold">{replace(staticText.get('yourOrder3'), '${ giftMembership }', giftMembership)}</span>
            <span>{staticText.get('yourOrder4')}</span>
          </>
        )}
      </p>
      {recipientEmail && deliveryDate && (
        <p className="gift-complete__text">
          <span>{staticText.get('yourGiftMembership1')}</span>
          <span className="gift-complete__bold">{yourGiftMembership2}</span>
          <span>{staticText.get('yourGiftMembership3')}</span>
          <br />
          <span className="gift-complete__bold">{yourGiftMembership4}</span>
          <span>{staticText.get('yourGiftMembership5')}</span>
          <span className="gift-complete__bold">{yourGiftMembership6}</span>
        </p>
      )}
      <p className="gift-complete__text">
        <span>{staticText.get('transactionReceipt1')}</span>
        {/* eslint-disable-next-line no-template-curly-in-string */}
        <span className="gift-complete__bold">{replace(staticText.get('transactionReceipt2'), '${ gifterEmail }', gifterEmail)}</span>
      </p>
      <p className="gift-complete__text">
        <span>{staticText.get('willExpire1')}</span>
        {/* eslint-disable-next-line no-template-curly-in-string */}
        <span className="gift-complete__bold">{replace(staticText.get('willExpire2'), '${ expireDate }', formattedExpireDate)}</span>
      </p>
      <p className="gift-complete__text">{staticText.get('link')}</p>
      <FormsyForm className="gift-complete__form">
        <TextInput
          className="gift-complete__link"
          label={staticText.get('giftLink')}
          name="giftLink"
          value={link}
          copyable
          readonly
          block
        />
        {mobileView
          ? null
          : <>
          <Button
            url={URL_HOME}
            className="gift-complete__btn"
            type={TYPES.PRIMARY}
          >{staticText.get('btnHome')}
          </Button>
          <Button
            url={giftMultipleCurrenciesActive ? getGiftLpUrl(userLanguage) : URL_GIFT_LP}
            className="gift-complete__btn"
            type={TYPES.SECONDARY}
          >{staticText.get('btnGift')}
          </Button>
          </>}
      </FormsyForm>
    </div>
  )
}

/**
 * @type {React.FC<PropTypes.InferProps<GiftCompletePagePropTypes>>}
 * @returns {React.ReactElement}
 * */
export function GiftCompletePage () {
  const userLanguage = useSelector(selectPrimaryLanguage)
  const mobileView = useSelector(selectIsBreakpoint(GIFT_MOBILE_BREAKPOINT))
  const staticText = useSelector(selectStaticTextByKey(staticTextStoreKey))
  const queryToken = useSelector(selectResolverQueryParamByKey('token'))
  let selectedPlan = useSelector(selectGiftPlan)
  const token = useSelector(selectGiftToken)
  const gift = useSelector(selectGift)
  const pageReady = !_isNil(token)
  const auth = useSelector(selectAuth)
  const authJwt = auth.get('jwt')
  const sku = useSelector(selectGiftSku)
  const formattedPlans = useGetPlanGridData({
    userLanguage,
    authJwt,
    page: BILLING_PLAN_GRID_LOCATION_GIFT,
  })

  if (planGraphQLActive) {
    selectedPlan = formattedPlans.find((gp) => gp.get('sku') === sku) || Map()
  }

  return (
    <>
      <GiftLayout
        main={
          <CompleteSection
            selectedPlan={selectedPlan}
            token={token || queryToken}
            mobileView={mobileView}
            staticText={staticText}
            gift={gift}
          />
        }
        rightSide={
          <GiftSelection
            selectedPlan={selectedPlan}
            showMobileTitle
          />
        }
        bottom={mobileView && (
          <div className="gift-complete__btns">
            <Button
              className="gift-complete__btn"
              type={TYPES.PRIMARY}
              url={URL_HOME}
            >{staticText.get('btnHome')}
            </Button>
            <Button
              className="gift-complete__btn"
              type={TYPES.SECONDARY}
              url={giftMultipleCurrenciesActive ? getGiftLpUrl(userLanguage) : URL_GIFT_LP}
            >{staticText.get('btnGift')}
            </Button>
          </div>
        )}
        pageTitle={staticText.get('title')}
        pageReady={pageReady}
        columnReverse
      />
    </>
  )
}

GiftCompletePage.propTypes = GiftCompletePagePropTypes

export default GiftCompletePage
